import React, { useState, useEffect } from 'react';
import { apprenantsAPI, configAPI } from '../services/api';
import { toast } from 'react-toastify';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../components/Icon';
import { colors } from '../theme/colors';

const SPC = () => {
  const [apprenants, setApprenants] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spcData, setSpcData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [apprenantsResponse, configResponse] = await Promise.all([
        apprenantsAPI.getAll({ limit: 1000 }),
        configAPI.get()
      ]);
      setApprenants(apprenantsResponse.data.apprenants || []);
      setConfig(configResponse.data);
      calculateSPC(apprenantsResponse.data.apprenants || [], configResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const calculateSPC = (data, configData) => {
    // Filtrer les apprenants avec insertion (métiers uniquement)
    const metiers = data.filter(a => {
      if (!a.typeFormation) return false;
      if (a.typeFormation.toLowerCase().includes('acculturation')) return false;
      if (!a.dateFin || !a.dateInsertion) return false;
      return true;
    });

    if (metiers.length < 30) {
      setSpcData({ insufficient: true, count: metiers.length });
      return;
    }

    // Calculer les délais d'insertion
    const delais = metiers.map(a => {
      const dateFin = new Date(a.dateFin);
      const dateInsertion = new Date(a.dateInsertion);
      return Math.round((dateInsertion - dateFin) / (1000 * 60 * 60 * 24));
    }).filter(d => d >= 0);

    if (delais.length === 0) {
      setSpcData({ insufficient: true });
      return;
    }

    // Moyenne et écart-type
    const moyenne = delais.reduce((a, b) => a + b, 0) / delais.length;
    const variance = delais.reduce((sum, d) => sum + Math.pow(d - moyenne, 2), 0) / delais.length;
    const ecartType = Math.sqrt(variance);

    // Limites de contrôle (3 sigma)
    const UCL = moyenne + (3 * ecartType);
    const LCL = Math.max(0, moyenne - (3 * ecartType));

    // Capabilité (objectif: ≤ 90 jours)
    const USL = configData?.USL || 90;
    const Cp = (USL - 0) / (6 * ecartType);
    const Cpk = Math.min(
      (USL - moyenne) / (3 * ecartType),
      (moyenne - 0) / (3 * ecartType)
    );

    // Données pour la carte X-bar
    const xbarData = delais.map((d, i) => ({
      index: i + 1,
      valeur: d
    })).slice(0, 50); // Limiter à 50 points pour le graphique

    // Données pour la carte P (proportion de sorties positives par groupe)
    const pChartData = []; // À implémenter si nécessaire

    setSpcData({
      moyenne,
      ecartType,
      UCL,
      LCL,
      Cp,
      Cpk,
      xbarData,
      delais,
      USL
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
        <Icon name="spc" size={28} className="mr-3 text-red-600" />
        SPC - Contrôle Statistique
      </h2>

      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-6">
        <p className="text-amber-800 flex items-center">
          <Icon name="info" size={18} className="mr-2" />
          <strong>Note:</strong> SPC sur formations métiers uniquement
        </p>
      </div>

      {spcData?.insufficient ? (
        <div className="section-card bg-white rounded-xl p-6 text-center">
          <p className="text-gray-600">
            Minimum 30 observations requises pour l'analyse SPC
            {spcData.count !== undefined && ` (${spcData.count} disponibles)`}
          </p>
        </div>
      ) : spcData ? (
        <>
          {/* Carte X-bar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="section-card bg-white rounded-xl p-6">
              <h3 className="font-bold mb-2 flex items-center text-gray-800">
                <Icon name="statistiques" size={20} className="mr-2 text-red-600" />
                Carte X-bar
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={spcData.xbarData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="index" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="valeur" stroke={colors.primary[600]} name="Délai (jours)" />
                  <Line
                    type="monotone"
                    dataKey={() => spcData.UCL}
                    stroke="#ef4444"
                    strokeDasharray="5 5"
                    name="UCL"
                  />
                  <Line
                    type="monotone"
                    dataKey={() => spcData.moyenne}
                    stroke="#22c55e"
                    strokeDasharray="5 5"
                    name="Moyenne"
                  />
                  <Line
                    type="monotone"
                    dataKey={() => spcData.LCL}
                    stroke="#3b82f6"
                    strokeDasharray="5 5"
                    name="LCL"
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="bg-red-50 rounded-lg p-2">
                  <p className="text-xs text-gray-500">UCL</p>
                  <p className="font-bold text-red-600">{spcData.UCL.toFixed(1)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2">
                  <p className="text-xs text-gray-500">Moyenne</p>
                  <p className="font-bold text-green-600">{spcData.moyenne.toFixed(1)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2">
                  <p className="text-xs text-gray-500">LCL</p>
                  <p className="font-bold text-blue-600">{spcData.LCL.toFixed(1)}</p>
                </div>
              </div>
            </div>

            {/* Capabilité */}
            <div className="section-card bg-white rounded-xl p-6">
              <h3 className="font-bold mb-2 flex items-center text-gray-800">
                <Icon name="target" size={20} className="mr-2 text-red-600" />
                Capabilité
              </h3>
              <p className="text-sm text-gray-500 mb-4">Objectif: ≤ {spcData.USL} jours</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500">Cp</p>
                  <p className="text-3xl font-bold">{spcData.Cp.toFixed(2)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-gray-500">Cpk</p>
                  <p className="text-3xl font-bold">{spcData.Cpk.toFixed(2)}</p>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Icon 
                    name={spcData.Cpk >= 1.33 ? 'success' : spcData.Cpk >= 1.0 ? 'warning' : 'error'} 
                    size={18} 
                    className={spcData.Cpk >= 1.33 ? 'text-green-600' : spcData.Cpk >= 1.0 ? 'text-amber-600' : 'text-red-600'} 
                  />
                  <span>
                    {spcData.Cpk >= 1.33
                      ? 'Processus capable (Cpk ≥ 1.33)'
                      : spcData.Cpk >= 1.0
                      ? 'Processus marginalement capable (1.0 ≤ Cpk < 1.33)'
                      : 'Processus non capable (Cpk < 1.0)'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Causes Spéciales */}
          <div className="section-card bg-white rounded-xl p-6">
            <h3 className="font-bold mb-4">⚠️ Causes Spéciales</h3>
            <div className="space-y-2">
              {spcData.delais.filter(d => d > spcData.UCL || d < spcData.LCL).length > 0 ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">
                    {spcData.delais.filter(d => d > spcData.UCL || d < spcData.LCL).length} point(s) hors limites de contrôle
                  </p>
                </div>
              ) : (
                <p className="text-gray-500">Aucune cause spéciale détectée</p>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="section-card bg-white rounded-xl p-6 text-center">
          <p className="text-gray-600">Aucune donnée disponible</p>
        </div>
      )}
    </div>
  );
};

export default SPC;
